import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LangSwitcher } from "./LangSwitcher";
import { Btn } from "./Shared";
import { useLang } from "../lib/lang";

const GROUPS = [
  { key: "translation", items: ["translation", "certified", "writing", "other"] },
  { key: "interpreting", items: ["interpreting", "consecutive", "online", "events"] },
];
const SINGLE = ["about", "blog", "contact"];

const Dropdown = ({ g, t, r }) => (
  <div className="group relative">
    <Link to={r(g.key)} className="flex items-center gap-1 py-2 text-[0.92rem] text-[var(--ink)]/85 transition-colors hover:text-[var(--ink)]" data-testid={`nav-${g.key}`}>
      {t.nav[g.key]} <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
    </Link>
    <div className="invisible absolute left-1/2 top-full z-50 w-72 -translate-x-1/2 translate-y-2 pt-3 opacity-0 transition-[opacity,transform] duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
      <div className="rounded-sm border border-[var(--line)] bg-[var(--paper)] p-2 shadow-[0_24px_60px_-20px_rgba(18,32,43,0.25)]">
        {g.items.map((k) => (
          <Link key={k} to={r(k)} className="flex items-center justify-between rounded-sm px-4 py-3 text-sm text-[var(--ink)] transition-colors hover:bg-[var(--paper-2)]" data-testid={`nav-sub-${k}`}>
            {t.nav[k]} <span className="h-1.5 w-1.5 rotate-45 border border-[var(--teal-ink)]" />
          </Link>
        ))}
      </div>
    </div>
  </div>
);

export const Header = () => {
  const { t, r } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${scrolled ? "border-b border-[var(--line)] bg-[var(--paper)]/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`} data-testid="site-header">
      <div className="mx-auto flex h-[72px] max-w-[1320px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
        <Link to={r("home")} aria-label="Translinked" data-testid="header-logo"><Logo /></Link>
        <nav className="hidden items-center gap-7 xl:flex" aria-label="Main">
          {GROUPS.map((g) => <Dropdown key={g.key} g={g} t={t} r={r} />)}
          {SINGLE.map((k) => (
            <Link key={k} to={r(k)} className="py-2 text-[0.92rem] text-[var(--ink)]/85 transition-colors hover:text-[var(--ink)]" data-testid={`nav-${k}`}>{t.nav[k]}</Link>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitcher className="hidden sm:flex" />
          <Btn to={r("upload")} className="hidden !px-5 !py-2.5 text-sm md:inline-flex" data-testid="header-quote-btn">{t.nav.cta}</Btn>
          <button onClick={() => setOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--ink)]/15 xl:hidden" aria-label={t.nav.menu} data-testid="mobile-menu-open"><Menu className="h-5 w-5" /></button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[60] flex flex-col bg-[var(--ink)] text-[var(--paper)] xl:hidden" initial={{ clipPath: "inset(0 0 100% 0)" }} animate={{ clipPath: "inset(0 0 0% 0)" }} exit={{ clipPath: "inset(0 0 100% 0)" }} transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }} data-testid="mobile-menu">
            <div className="flex h-[72px] items-center justify-between px-5 sm:px-8">
              <Logo light />
              <button onClick={() => setOpen(false)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25" aria-label="Close" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 pb-10 sm:px-8" data-lenis-prevent>
              {GROUPS.map((g) => (
                <div key={g.key} className="border-b border-white/10 py-5">
                  <p className="eyebrow mb-3 text-[#9FD3CD]">{t.nav[g.key]}</p>
                  {g.items.map((k) => <Link key={k} to={r(k)} className="block py-1.5 font-display text-2xl" data-testid={`m-nav-${k}`}>{t.nav[k]}</Link>)}
                </div>
              ))}
              <div className="py-5">
                {SINGLE.map((k) => <Link key={k} to={r(k)} className="block py-1.5 font-display text-2xl" data-testid={`m-nav-${k}`}>{t.nav[k]}</Link>)}
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <LangSwitcher light />
                <Btn to={r("upload")} variant="teal" data-testid="m-quote-btn">{t.nav.cta}</Btn>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
